import React, { useState } from "react";
import axios from "axios";

export default function Contact() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setStatus({ type: "", message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.name || !form.email || !form.message) {
      setStatus({
        type: "error",
        message: "Please fill in your name, email and message.",
      });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE}/api/contact`, form);
      setStatus({
        type: "success",
        message: "Thank you! Your message has been sent.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus({
        type: "error",
        message:
          "Something went wrong while sending your message. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4">
        {/* Hero */}
        <div className="text-center mb-10">
          <p className="uppercase tracking-[0.25em] text-xs text-gray-500 mb-3">
            Get in touch
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-3">
            Let&apos;s design your perfect spa experience
          </h1>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Whether you&apos;re opening a new salon, upgrading your spa, or
            looking for premium massage furniture, we&apos;re here to help with
            recommendations, quotes and shipping details.
          </p>
        </div>

        {/* Layout: info + form */}
        <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          {/* Left: contact info */}
          <div className="space-y-5">
            <div className="bg-white/80 border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">
                Contact details
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Reach out directly or use the form and our team will respond
                within 1–2 business days.
              </p>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  <span className="font-medium text-gray-900">Email:</span>{" "}
                  <a
                    href="mailto:info@relaxb.com"
                    className="text-blue-600 hover:underline"
                  >
                    info@relaxb.com
                  </a>
                </p>
                <p>
                  <span className="font-medium text-gray-900">Phone:</span>{" "}
                  +1 (555) 123-4567
                </p>
                <p>
                  <span className="font-medium text-gray-900">
                    Based in:
                  </span>{" "}
                  Canada
                </p>
              </div>
            </div>

            <div className="bg-white/80 border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Typical questions we help with
              </h3>
              <ul className="text-sm text-gray-600 space-y-1.5 list-disc list-inside">
                <li>Which massage chair is right for my space?</li>
                <li>Bulk pricing for salons and spas</li>
                <li>Shipping timelines & warranty details</li>
                <li>Custom colours and configurations</li>
              </ul>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white/90 border border-gray-100 rounded-3xl p-6 md:p-7 shadow-md">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Send us a message
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Tell us a bit about what you&apos;re planning and we&apos;ll get
              back to you with next steps.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name + Email */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Name<span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 focus:border-black"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email<span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 focus:border-black"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              {/* Phone + Subject */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 focus:border-black"
                    placeholder="+1 ..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 focus:border-black"
                    placeholder="Massage chairs for my spa"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  How can we help?<span className="text-rose-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 focus:border-black resize-none"
                  placeholder="Tell us about your salon, spa or home setup..."
                  required
                />
              </div>

              {/* Status message */}
              {status.message && (
                <div
                  className={`text-xs rounded-xl px-3 py-2 ${
                    status.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                      : "bg-red-50 text-red-700 border border-red-100"
                  }`}
                >
                  {status.message}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className={`w-full rounded-full px-4 py-3 text-sm font-medium text-white shadow-sm transition ${
                  submitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-black hover:bg-gray-900"
                }`}
              >
                {submitting ? "Sending..." : "Send message"}
              </button>

              <p className="text-[11px] text-gray-400 mt-1 text-center">
                We respect your privacy. Your details are only used to respond
                to your enquiry.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
